"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import type { ColorSpace } from "@/lib/color";
import { type Field, GAMUT_VIEW as VIEW } from "@/lib/gamut-projection";
const OUTLINED_SPACES = ["p3", "srgb"] as const satisfies readonly ColorSpace[];
const paint = (canvas: HTMLCanvasElement, field: Field) => {
  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const {
    clientWidth,
    clientHeight
  } = canvas;
  canvas.width = Math.round(clientWidth * dpr);
  canvas.height = Math.round(clientHeight * dpr);
  context.setTransform(clientWidth / VIEW.width * dpr, 0, 0, clientHeight / VIEW.height * dpr, 0, 0);
  context.clearRect(0, 0, VIEW.width, VIEW.height);
  const source = document.createElement("canvas");
  source.width = field.width;
  source.height = field.height;
  source.getContext("2d")?.putImageData(new ImageData(field.pixels, field.width, field.height), 0, 0);
  const {
    rect
  } = field;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, rect.x, rect.y, rect.width, rect.height);
};
interface GamutSliceProps {
  field: Field;
  outline: (space: ColorSpace) => string;
}
export function GamutSlice(t0) {
  const $ = _c(8);
  const {
    field,
    outline
  } = t0;
  const canvasRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== field) {
    t1 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const repaint = () => paint(canvas, field);
      repaint();
      const observer = new ResizeObserver(repaint);
      observer.observe(canvas);
      return () => observer.disconnect();
    };
    t2 = [field];
    $[0] = field;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <canvas className="absolute inset-0 h-full w-full" ref={canvasRef} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== outline) {
    t4 = OUTLINED_SPACES.map(space => <path d={outline(space)} fill="none" key={space} stroke="white" strokeDasharray={space === "p3" ? "6 4" : undefined} strokeLinecap="round" strokeWidth="1" vectorEffect="non-scaling-stroke" />);
    $[4] = outline;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <>{t3}<svg aria-hidden="true" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}>{t4}</svg></>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}